import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, Flame, Inbox, CalendarDays, MessageSquare, UserCheck, BookOpen, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { PageTitle } from "./DashboardLayout";

export default function Overview() {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/dashboard/stats").then(r => r.data) });
  const { data: billing } = useQuery({ queryKey: ["billing-stats"], queryFn: () => api.get("/dashboard/billing").then(r => r.data) });
  const { data: recent } = useQuery({ queryKey: ["recent"], queryFn: () => api.get("/dashboard/recent").then(r => r.data) });

  const cards = [
    { label: "New Leads", value: stats?.newLeads, icon: Users, to: "/dashboard/leads" },
    { label: "Hot Leads", value: stats?.hotLeads, icon: Flame, to: "/dashboard/leads" },
    { label: "Open Requests", value: stats?.openRequests, icon: Inbox, to: "/dashboard/requests" },
    { label: "Appointment Requests", value: stats?.appointmentsRequested, icon: CalendarDays, to: "/dashboard/appointments" },
    { label: "AI Conversations", value: stats?.aiConversations, icon: MessageSquare, to: "/dashboard/ai" },
    { label: "Customers", value: stats?.customers, icon: UserCheck, to: "/dashboard/users" },
    { label: "Total Leads", value: stats?.totalLeads, icon: Users, to: "/dashboard/leads" },
    { label: "Knowledge Drafts", value: stats?.knowledgeDrafts, icon: BookOpen, to: "/dashboard/knowledge" },
  ];

  return (
    <div>
      <PageTitle title="Dashboard" subtitle="Your business at a glance" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link to={c.to} key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}
            className="rounded-xl bg-white border border-slate-200 p-5 hover:shadow-md hover:border-electric transition-all group">
            <div className="flex items-center justify-between">
              <span className="h-9 w-9 rounded-lg bg-electric/10 text-electric grid place-items-center"><c.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /></span>
              <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-electric transition-colors" />
            </div>
            <p className="mt-3 text-3xl font-heading font-bold text-navy">{c.value ?? "—"}</p>
            <p className="text-sm text-slate-500">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {[["Outstanding", billing?.outstanding, "text-amber-600"], ["Overdue", billing?.overdue, "text-red-600"], ["Collected", billing?.collected, "text-green-600"], ["Open Invoices", billing?.openInvoices, "text-navy", true]].map(([label, val, color, count]) => (
          <Link to="/dashboard/invoices" key={label} className="rounded-xl bg-white border border-slate-200 p-5 hover:border-electric transition-all" data-testid={`billing-${label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-xs font-mono uppercase tracking-wider text-metallic">{label}</p>
            <p className={`mt-2 text-2xl font-heading font-bold ${color}`}>{val == null ? "—" : count ? val : `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}</p>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-heading font-semibold text-navy">Recent Leads</h3><Link to="/dashboard/leads" className="text-sm text-electric">View all</Link></div>
          <div className="space-y-2">
            {(recent?.leads || []).length === 0 ? <p className="text-sm text-slate-400">No leads yet.</p> :
              (recent?.leads || []).map((l) => (
                <div key={l.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div><p className="text-sm font-medium text-navy">{l.firstName} {l.lastName}</p><p className="text-xs text-slate-500">{l.serviceCategory || "General"}</p></div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{l.status}</span>
                </div>
              ))}
          </div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-heading font-semibold text-navy">Recent Requests</h3><Link to="/dashboard/requests" className="text-sm text-electric">View all</Link></div>
          <div className="space-y-2">
            {(recent?.requests || []).length === 0 ? <p className="text-sm text-slate-400">No requests yet.</p> :
              (recent?.requests || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div><p className="text-sm font-medium text-navy">{r.fullName}</p><p className="text-xs text-slate-500">{r.serviceType}</p></div>
                  <span className="text-xs font-mono text-slate-400">{r.requestNumber}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
